import { createTheme } from '@mui/material/styles';
import muiShadows from './shadows';
import { colors, typography, radius } from 'theme/tokens';

const { fontSize, fontWeight, letterSpacing } = typography;

const headingFont = typography.fontFamily.inter;
const bodyFont = typography.fontFamily.inter;


export const NO_AUTOFILL = 'new-password';

const theme = createTheme({
  palette: {
    primary: {
      main: colors.brand.primary,
      light: colors.brand.primaryLight,
      dark: colors.brand.primaryDark,
      contrastText: colors.brand.contrastText,
    },
    secondary: {
      main: colors.brand.secondary,
      light: colors.brand.secondaryLight,
      dark: colors.brand.secondaryDark,
      contrastText: colors.brand.secondaryContrastText,
    },
    success: {
      main: colors.status.success,
      contrastText: colors.text.inverse,
    },
    background: {
      default: colors.background.default,
      paper: colors.background.paper,
    },
    text: {
      primary: colors.text.primary,
      secondary: colors.text.secondary,
      disabled: colors.text.disabled,
    },
    divider: colors.divider,
    // Sidebar chrome tokens (dark violet panel): the collapse / expand
    // button's icon colour at rest and on hover.
    sidebar: {
      icon: colors.sidebarChrome.icon,
      iconHover: colors.sidebarChrome.iconHover,
    },
    neutral: colors.neutralScale,
  },
  typography: {
    fontFamily: bodyFont,
    h1: { fontFamily: headingFont, fontWeight: fontWeight.bold, fontSize: fontSize[32], letterSpacing: letterSpacing.tighter },
    h2: { fontFamily: headingFont, fontWeight: fontWeight.bold, fontSize: fontSize[24], letterSpacing: letterSpacing.tight },
    h3: { fontFamily: headingFont, fontWeight: fontWeight.bold, fontSize: fontSize[20], letterSpacing: letterSpacing.tight },
    h4: { fontFamily: headingFont, fontWeight: fontWeight.semiBold, fontSize: fontSize[18] },
    h5: { fontFamily: headingFont, fontWeight: fontWeight.semiBold, fontSize: fontSize[16] },
    h6: { fontFamily: headingFont, fontWeight: fontWeight.semiBold, fontSize: fontSize[14] },
    subtitle1: { fontSize: fontSize[16], fontWeight: fontWeight.medium },
    subtitle2: { fontSize: fontSize[14], fontWeight: fontWeight.medium },
    body1: { fontSize: fontSize[14] },
    body2: { fontSize: fontSize[12] },
    // Shared booking-detail tokens, identical in both portals' themes:
    // `label` — the small uppercase field labels (LEAD PASSENGER, CHECK-IN…)
    // `tag`   — chip / status-badge / tag text.
    label: { fontSize: fontSize[10], fontWeight: fontWeight.semiBold, letterSpacing: '0.05em', textTransform: 'uppercase', lineHeight: typography.lineHeight.loose },
    tag: { fontSize: fontSize[12], fontWeight: fontWeight.semiBold, lineHeight: typography.lineHeight.relaxed },
    button: { fontFamily: headingFont, textTransform: 'none', fontWeight: fontWeight.semiBold },
  },
  shape: { borderRadius: radius.md },
  shadows: muiShadows,
  components: {
    // Full mapping (not just the two new ones): this default prop REPLACES
    // MUI's own map, so the built-in variants must be listed too.
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          h1: 'h1', h2: 'h2', h3: 'h3', h4: 'h4', h5: 'h5', h6: 'h6',
          subtitle1: 'h6', subtitle2: 'h6', body1: 'p', body2: 'p', inherit: 'p',
          label: 'span', tag: 'span',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: radius.md, padding: '8px 20px' },
        containedPrimary: { '&:hover': { backgroundColor: colors.brand.primaryDark } },
      },
    },
    MuiCard: {
      styleOverrides: { root: { borderRadius: radius.xl, boxShadow: muiShadows[2] } },
    },
    MuiChip: {
      styleOverrides: { root: { borderRadius: radius.md, fontFamily: headingFont, fontWeight: fontWeight.semiBold } },
    },
    MuiPaper: {
      styleOverrides: { root: { borderRadius: radius.xl } },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            fontFamily: headingFont,
            fontWeight: fontWeight.semiBold,
            backgroundColor: colors.background.default,
            color: colors.text.secondary,
            fontSize: fontSize[12],
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: { root: { fontFamily: headingFont, fontWeight: fontWeight.medium } },
    },
    MuiFormLabel: {
      styleOverrides: {
        asterisk: { color: colors.status.error },
      },
    },
    MuiDialogTitle: {
      styleOverrides: { root: { fontFamily: headingFont } },
    },
    MuiTextField: {
      defaultProps: { autoComplete: NO_AUTOFILL },
    },
  },
});

export default theme;
